import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useGameStore from '../../stores/gameStore';
import useUIStore from '../../stores/uiStore';
import { useAdventureStyle } from '../../hooks/useAdventureStyle';

interface CheckboxFieldProps {
  variableName: string;
  label?: string;
  checkedValue?: string;
  uncheckedValue?: string;
  defaultValue?: string;
  /** Adventure/scene option button color (from style cascade). */
  customColor?: string;
  className?: string;
  ariaLabel?: string;
}

/**
 * Labelled checkbox control bound to a string variable.
 * Submits checked/unchecked values via submitUserInput (same pipeline as Spinner).
 * Checked fill uses adventure option button color with a black tick.
 */
const CheckboxField: React.FC<CheckboxFieldProps> = ({
  variableName,
  label,
  checkedValue = '1',
  uncheckedValue = '0',
  defaultValue,
  customColor,
  className,
  ariaLabel
}) => {
  const { buttonColor } = useAdventureStyle();
  const fillColor = customColor || buttonColor;

  const storedValue = useGameStore(
    useCallback(
      (state) => state.userVariableValues[variableName]?.value ?? '',
      [variableName]
    )
  );
  const isSessionReady = useGameStore(
    useCallback((state) => Boolean(state.currentSession && state.currentPlayer), [])
  );
  const submitUserInput = useGameStore((state) => state.submitUserInput);
  const uiStore = useUIStore();

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Optimistic value shown immediately on toggle; cleared when store confirms. */
  const [optimisticValue, setOptimisticValue] = useState<string | null>(null);
  const hasAttemptedInitialSyncRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, [variableName]);

  const resolvedDefault = useMemo(() => {
    const trimmed = (defaultValue ?? '').toString().trim();
    if (trimmed === checkedValue || trimmed === uncheckedValue) {
      return trimmed;
    }
    return uncheckedValue;
  }, [defaultValue, checkedValue, uncheckedValue]);

  const effectiveStored = useMemo(() => {
    const stored = (storedValue ?? '').toString().trim();
    if (stored === checkedValue || stored === uncheckedValue) {
      return stored;
    }
    if (stored === '') {
      return resolvedDefault;
    }
    // Unknown stored value: treat as unchecked for display, but still allow toggle.
    return uncheckedValue;
  }, [storedValue, checkedValue, uncheckedValue, resolvedDefault]);

  useEffect(() => {
    if (optimisticValue !== null && (storedValue ?? '').toString().trim() === optimisticValue) {
      setOptimisticValue(null);
    }
  }, [storedValue, optimisticValue]);

  const currentValue =
    optimisticValue !== null &&
    (optimisticValue === checkedValue || optimisticValue === uncheckedValue)
      ? optimisticValue
      : effectiveStored;
  const isChecked = currentValue === checkedValue;

  useEffect(() => {
    setError(null);
  }, [storedValue]);

  const submitValue = useCallback(
    async (newValue: string, options?: { force?: boolean }) => {
      if (!options?.force && newValue === storedValue) return;

      try {
        if (!isMountedRef.current) {
          return;
        }
        setIsSaving(true);
        setError(null);
        await submitUserInput(variableName, newValue);
        if (!isMountedRef.current) {
          return;
        }
      } catch (err) {
        if (!isMountedRef.current) {
          return;
        }
        const message =
          err instanceof Error ? err.message : 'Failed to update value';
        setError(message);
        setOptimisticValue(null);
        hasAttemptedInitialSyncRef.current = false;
        uiStore.setError({
          code: 'CHECKBOX_ERROR',
          message
        });
      } finally {
        if (isMountedRef.current) {
          setIsSaving(false);
        }
      }
    },
    [variableName, storedValue, submitUserInput, uiStore]
  );

  // Persist default/unchecked when the variable is still empty so Conditional scripts see it.
  useEffect(() => {
    if (!isSessionReady) {
      return;
    }
    const stored = (storedValue ?? '').toString().trim();
    if (stored !== '') {
      return;
    }
    if (hasAttemptedInitialSyncRef.current) {
      return;
    }
    const initialValue = (currentValue ?? '').toString().trim();
    if (!initialValue) {
      return;
    }
    hasAttemptedInitialSyncRef.current = true;
    setOptimisticValue(initialValue);
    void submitValue(initialValue, { force: true });
  }, [isSessionReady, storedValue, currentValue, submitValue]);

  const toggle = useCallback(() => {
    if (isSaving) return;
    const newValue = isChecked ? uncheckedValue : checkedValue;
    setOptimisticValue(newValue);
    void submitValue(newValue);
  }, [isSaving, isChecked, uncheckedValue, checkedValue, submitValue]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        toggle();
      }
    },
    [toggle]
  );

  const rootClassName = useMemo(() => {
    const classes = ['rich-text-checkbox'];
    if (className) classes.push(className);
    if (error) classes.push('rich-text-checkbox--error');
    if (isSaving) classes.push('rich-text-checkbox--saving');
    return classes.join(' ');
  }, [className, error, isSaving]);

  const rootStyle = useMemo(
    () =>
      ({
        ['--checkbox-fill' as string]: fillColor
      }) as React.CSSProperties,
    [fillColor]
  );

  const accessibleName = ariaLabel ?? (label && label.trim() !== '' ? label : variableName);
  const inputId = `checkbox-${variableName}`;

  return (
    <div
      className={rootClassName}
      style={rootStyle}
      data-variable={variableName}
      data-testid={`checkbox-${variableName}`}
    >
      <label className="rich-text-checkbox__label" htmlFor={inputId}>
        {label && label.trim() !== '' && (
          <span className="rich-text-checkbox__text">{label}</span>
        )}
        <input
          id={inputId}
          type="checkbox"
          className="rich-text-checkbox__input"
          checked={isChecked}
          disabled={isSaving}
          onChange={toggle}
          onKeyDown={handleKeyDown}
          aria-label={accessibleName}
        />
      </label>

      {error && (
        <span
          className="rich-text-checkbox__error-message"
          role="status"
          aria-live="assertive"
          data-testid={`checkbox-${variableName}-error`}
        >
          {error}
        </span>
      )}
    </div>
  );
};

export default CheckboxField;
